// Button.jsx
import { forwardRef } from 'react';
import { motion } from 'framer-motion';

/**
 * Inline-style Button component (compatible API with your previous Button)
 * - Uses CSS variables for colors so it matches Login.jsx visual language
 * - Exports: default Button, IconButton, ButtonGroup
 */

const sizeMap = {
  xs: { padding: '6px 10px', fontSize: '12px', minHeight: 28, iconSize: 12 },
  sm: { padding: '8px 12px', fontSize: '14px', minHeight: 32, iconSize: 14 },
  md: { padding: '10px 16px', fontSize: '14px', minHeight: 40, iconSize: 16 },
  lg: { padding: '14px 20px', fontSize: '16px', minHeight: 44, iconSize: 20 },
  xl: { padding: '16px 24px', fontSize: '18px', minHeight: 48, iconSize: 22 }
};

const variantStyles = {
  primary: (isDisabled) => ({
    background: isDisabled ? 'var(--primary-400)' : 'var(--primary-600)',
    color: 'white',
    border: `1px solid ${isDisabled ? 'var(--primary-400)' : 'var(--primary-600)'}`,
    boxShadow: '0 6px 18px rgba(59,130,246,0.08)'
  }),
  secondary: () => ({
    background: 'white',
    color: 'var(--gray-900)',
    border: '1px solid var(--gray-300)'
  }),
  success: (isDisabled) => ({
    background: isDisabled ? 'var(--success-400)' : 'var(--success-600)',
    color: 'white',
    border: `1px solid ${isDisabled ? 'var(--success-400)' : 'var(--success-600)'}`
  }),
  warning: (isDisabled) => ({
    background: isDisabled ? 'var(--warning-400)' : 'var(--warning-600)',
    color: 'white',
    border: `1px solid ${isDisabled ? 'var(--warning-400)' : 'var(--warning-600)'}`
  }),
  error: (isDisabled) => ({
    background: isDisabled ? 'var(--error-400)' : 'var(--error-600)',
    color: 'white',
    border: `1px solid ${isDisabled ? 'var(--error-400)' : 'var(--error-600)'}`
  }),
  ghost: () => ({
    background: 'transparent',
    color: 'var(--gray-700)',
    border: '1px solid transparent'
  }),
  outline: () => ({
    background: 'transparent',
    color: 'var(--primary-600)',
    border: '1px solid var(--primary-600)'
  })
};

const baseStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  borderRadius: 8,
  cursor: 'pointer',
  userSelect: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 120ms ease, box-shadow 120ms ease'
};

const LoadingSpinner = ({ sizePx, color = 'white' }) => (
  <motion.span
    style={{
      width: sizePx,
      height: sizePx,
      borderRadius: '50%',
      borderStyle: 'solid',
      borderWidth: Math.max(2, Math.round(sizePx / 8)),
      borderColor: `${color}`,
      borderTopColor: 'transparent',
      display: 'inline-block'
    }}
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
  />
);

const Button = forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      fullWidth = false,
      leftIcon = null,
      rightIcon = null,
      className = '',
      onClick,
      type = 'button',
      style = {},
      ...props
    },
    ref
  ) => {
    const sz = sizeMap[size] || sizeMap.md;
    const isDisabled = disabled || loading;

    const variantStyle = (variantStyles[variant] || variantStyles.primary)(isDisabled);

    const mergedStyle = {
      ...baseStyle,
      ...variantStyle,
      padding: sz.padding,
      fontSize: sz.fontSize,
      minHeight: sz.minHeight,
      width: fullWidth ? '100%' : 'auto',
      opacity: isDisabled ? 0.65 : 1,
      pointerEvents: isDisabled ? 'none' : undefined,
      ...style
    };

    const iconWrapperStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: leftIcon ? 8 : 0,
      marginLeft: rightIcon ? 8 : 0,
      lineHeight: 0
    };

    return (
      <motion.button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={isDisabled}
        title={props.title}
        style={mergedStyle}
        whileHover={!isDisabled ? { scale: 1.02 } : {}}
        whileTap={!isDisabled ? { scale: 0.98 } : {}}
        transition={{ duration: 0.08 }}
        className={className}
        {...props}
      >
        {/* Press ripple overlay (subtle) */}
        <motion.span
          style={{
            position: 'absolute',
            inset: 0,
            background: 'white',
            opacity: 0,
            pointerEvents: 'none'
          }}
          whileTap={{ opacity: 0.05 }}
          transition={{ duration: 0.08 }}
        />

        {/* Left icon / loading spinner */}
        {loading ? (
          <span style={{ marginRight: 8 }}>
            <LoadingSpinner sizePx={sz.iconSize} color={variantStyle.color || 'white'} />
          </span>
        ) : (
          leftIcon && <span style={iconWrapperStyle}>{leftIcon}</span>
        )}

        <span style={{ opacity: loading ? 0.85 : 1 }}>{children}</span>

        {!loading && rightIcon && <span style={{ marginLeft: 8 }}>{rightIcon}</span>}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

// IconButton: lightweight wrapper that uses Button and provides sizing
export const IconButton = forwardRef(
  (
    { children, variant = 'ghost', size = 'md', tooltip = '', className = '', ...props },
    ref
  ) => {
    const sz = sizeMap[size] || sizeMap.md;
    // set a square layout for icon button
    const iconBtnStyle = {
      padding: 0,
      width: sz.minHeight,
      height: sz.minHeight,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      background: 'transparent'
    };

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        style={{ ...iconBtnStyle }}
        title={tooltip}
        className={className}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';

// ButtonGroup: small helper - kept className to allow layout control in parent
export const ButtonGroup = ({ children, className = '', orientation = 'horizontal' }) => {
  const groupStyle =
    orientation === 'vertical'
      ? { display: 'flex', flexDirection: 'column', gap: 0 }
      : { display: 'inline-flex', gap: 0 };

  // We won't attempt pixel-perfect joined borders; keep layout simple and inline
  return (
    <div style={groupStyle} className={className} role="group" aria-label="Button group">
      {children}
    </div>
  );
};

export default Button;
